import { type Failure, RequestFailed, readJson, request } from './request'

/**
 * The ways into foreign systems, as the service describes them.
 *
 * Everything this screen needs arrives in **one** answer, and that is the shape of the endpoint rather
 * than a convenience taken here: what can be connected at all, what this build has a connector for, and
 * what has been set up are three facts that mean nothing apart. An installation that keeps no
 * credentials has to say so instead of drawing a form whose "save" could never work, and it can only
 * say so if the first of the three arrives with the other two.
 *
 * **No password ever comes back.** There is deliberately no field for one anywhere below and no call
 * that answers with one — the promise given when somebody types it in ("it is never shown to you
 * again") is kept by there being nothing to show, not by a screen choosing not to draw it.
 */

/** How a sync ended. Three answers, because each of them is something different to do about it. */
export type SyncOutcome = 'FINISHED' | 'ACCESS_REFUSED' | 'NOT_REACHABLE'

export interface SyncRun {
  readonly startedAt: string
  /** When it ended, or `null` while it is still going — which is what {@link SyncRun.running} says. */
  readonly finishedAt: string | null
  readonly outcome: SyncOutcome | null
  /** How many files became documents that were not there before. */
  readonly added: number
  /** How many files were a changed version of a document that was already there. */
  readonly newVersions: number
  /** How many documents were marked as no longer being in the other system. Never deleted ones. */
  readonly goneFromSource: number
  /** How many files were passed over — too large for this installation, or unreadable. */
  readonly skipped: number
  /**
   * What went wrong, in the other system's own words. Empty for a run that finished.
   *
   * `outcome` says what to *do* about it; this says what was actually said. Without it every failure
   * reads as "it did not answer" — which covers a server that is down and a request the other side
   * refused for a reason somebody can act on, and those want different things done.
   */
  readonly problem: string
  readonly running: boolean
}

export interface Connection {
  readonly id: string
  /** Which kind of system it reaches, as the service names it: `ILIAS`. */
  readonly kind: string
  readonly address: string
  /** Which client of that installation. Empty for a system that has no such notion. */
  readonly tenant: string
  /** Who somebody is over there — never who they are here. */
  readonly username: string
  readonly createdAt: string
  readonly updatedAt: string
  /**
   * Whether the password kept for it can still be read.
   *
   * False after the installation's key was changed or lost. It is not a fault to be repaired from
   * here: the only honest answer is to ask for the password again, and that is what the screen does.
   */
  readonly usable: boolean
  /** How the last sync went, or `null` while none has been started. */
  readonly lastRun: SyncRun | null
}

/**
 * One kind of system this build can reach.
 *
 * `brings` is the field the screen turns on: a connection that brings files is set up by ticking
 * folders, one that brings a timetable by choosing a period and whose plan. Working that out from the
 * name would mean keeping a list of MyLib's kinds over here — the very list that would be forgotten
 * the day a third one arrives.
 */
export interface System {
  /** The name to send back in a request: `ILIAS`, `WEB_UNTIS`. */
  readonly kind: string
  /** What a document or an appointment says it came out of: `ILIAS`, `WebUntis`. */
  readonly systemName: string
  readonly brings: 'FILES' | 'TIMETABLE'
}

export interface ExternalAccess {
  /** Which systems this build can reach. Empty is a perfectly ordinary answer. */
  readonly kinds: readonly System[]
  readonly connections: readonly Connection[]
}

/** Whose plan a timetable connection fetches: a person over there, or a class. */
export interface TimetableElement {
  readonly kind: 'PERSON' | 'CLASS'
  readonly id: string
  readonly name: string
}

/**
 * What is chosen for a connection that brings a timetable.
 *
 * **There is one date and it is the beginning.** Nobody is asked how far into the future their own
 * timetable goes: how far the plan reaches is the source's to say, and it is asked on every run.
 */
export interface TimetableWindow {
  /** The first day fetched, as `YYYY-MM-DD`. Everything from here on is taken over. */
  readonly from: string
  /** Whose plan, or `null` for "whoever these credentials are". */
  readonly element: TimetableElement | null
  readonly mergeAdjacent: boolean
  readonly chosenAt: string
}

/**
 * What is chosen, and what would be offered if nothing were.
 *
 * `window` is `null` while nothing has been chosen, which is an ordinary state: the screen then shows
 * the two suggested days filled in rather than two empty date fields.
 */
export interface TimetablePlan {
  readonly window: TimetableWindow | null
  /** The day a connection would fetch from before anybody has said otherwise. */
  readonly suggestedFrom: string
}

/** Whose plan this connection could fetch — the whole case distinction, answered over there. */
export interface WhoseTimetable {
  readonly person: TimetableElement | null
  readonly choices: readonly TimetableElement[]
}

/** What is being chosen for a timetable connection. All of it at once — it is read together. */
export interface ChosenTimetable {
  readonly from: string
  readonly elementKind: string | null
  readonly elementId: string | null
  readonly elementName: string
  readonly mergeAdjacent: boolean
}

/** What is needed to set one up. The password goes with the request that keeps it, and nowhere else. */
export interface NewConnection {
  readonly kind: string
  readonly address: string
  readonly tenant: string
  readonly username: string
  readonly password: string
}

/** What is being corrected on one that exists. */
export interface ChangedConnection {
  readonly address: string
  readonly tenant: string
  readonly username: string
  /**
   * A `null` password keeps the one that is stored.
   *
   * That is what makes a mistyped user name correctable by somebody who was promised never to be
   * shown their password again — without it, every correction would be a re-entry.
   */
  readonly password: string | null
}

/**
 * One thing found in a foreign system: a folder to walk into, or a file that lies in one.
 *
 * A path is the whole of a file's identity over there — the other side hands out no object id — so it
 * is what everything here is keyed by, and it is never tidied up for display: segments joined with
 * `/`, no slash at either end, and the empty string for the top of what an account can see.
 */
export interface SourceEntry {
  readonly path: string
  /** What the other system calls it. Shown as it stands, and never trusted to be a file name. */
  readonly name: string
  readonly folder: boolean
  /** How large the file is, where the other side said. `null` when it did not, and for a folder. */
  readonly sizeInBytes: number | null
  readonly changedAt: string | null
  readonly mediaType: string | null
}

/**
 * One level of the foreign tree: where it was read, and what lies directly in it.
 *
 * One level and never a subtree, because there is no subtree call and there cannot be one — the
 * systems MyLib reaches answer a request for the whole tree with exactly one level anyway. So every
 * folder somebody opens is one request, which is also why nothing here fetches ahead.
 */
export interface Level {
  readonly path: string
  readonly entries: readonly SourceEntry[]
}

/** One folder somebody has ticked, and when. Folders only — see {@link chooseFolder}. */
export interface Chosen {
  readonly path: string
  readonly chosenAt: string
}

const CONNECTIONS = '/api/connections'

export async function fetchExternalAccess(signal?: AbortSignal): Promise<ExternalAccess> {
  const response = await request(CONNECTIONS, { signal })
  return readJson<ExternalAccess>(response)
}

/**
 * Sets one up — and the service tries it before it keeps it.
 *
 * So a refusal here is not "saving failed": it is one of the three answers that name a value, and the
 * screen puts it beside that value. See {@link fieldRefused}.
 */
export async function addConnection(values: NewConnection): Promise<Connection> {
  return (await change(CONNECTIONS, 'POST', values)) as Connection
}

/** Corrects one, and again only keeps what worked. */
export async function changeConnection(id: string, values: ChangedConnection): Promise<Connection> {
  return (await change(`${CONNECTIONS}/${encodeURIComponent(id)}`, 'PUT', values)) as Connection
}

/** Tries one that is already set up, with the password it holds. */
export async function testConnection(id: string): Promise<Connection> {
  return (await change(`${CONNECTIONS}/${encodeURIComponent(id)}/test`, 'POST')) as Connection
}

/**
 * Starts a sync by hand, and comes back before it is done.
 *
 * The service takes the run on and lets it go — `202` with the connection, whose last run is the one
 * that has just started — because a semester's material takes minutes and nothing on this side is going
 * to sit and wait for it. So what comes back is what the screen shows at once: a connection that is
 * syncing now. A connection that is already syncing answers with the run that is going on, rather than
 * sending a second walk over the same tree.
 *
 * **It never deletes anything**, here or over there. It adds what is new, adds a version to what has
 * changed, and marks what has disappeared from the other system — that is the whole of it.
 */
export async function sync(id: string): Promise<Connection> {
  return (await change(`${CONNECTIONS}/${encodeURIComponent(id)}/sync`, 'POST')) as Connection
}

/**
 * Removes one.
 *
 * **Only the syncing stops.** Everything taken over stays in the library, which is why the question
 * asked before this says so rather than merely asking whether somebody is sure.
 */
export async function removeConnection(id: string): Promise<void> {
  await change(`${CONNECTIONS}/${encodeURIComponent(id)}`, 'DELETE')
}

/**
 * Reads one level of a foreign system, with the credentials the connection holds.
 *
 * The empty path is the top of what those credentials can see. **This goes over the wire to somebody
 * else's server**, which is the reason the screen asks for a level only when somebody opens one and
 * never for a connection merely being looked at: a folder costs the better part of a second over
 * there, and a screen that fetched ahead would spend a dozen of them on folders nobody opened.
 */
export async function browse(id: string, path: string, signal?: AbortSignal): Promise<Level> {
  const at = `${CONNECTIONS}/${encodeURIComponent(id)}/browse?path=${encodeURIComponent(path)}`
  return ask<Level>(at, signal)
}

/** Which folders are ticked. A read of MyLib's own database, so it costs the other system nothing. */
export async function chosenFolders(id: string, signal?: AbortSignal): Promise<readonly Chosen[]> {
  return ask<readonly Chosen[]>(`${CONNECTIONS}/${encodeURIComponent(id)}/selection`, signal)
}

/**
 * Ticks a folder, and everything below it, now and in every sync to come.
 *
 * **Folders and never single files**, which is not a limitation of this call but the point of it: a
 * file chosen by hand cannot bring the next one with it, and material that appears over there in
 * November is the whole reason a connection exists at all.
 *
 * The service reads the path over there before it keeps it, so a path typed with a slip in it comes
 * back as `no-such-path` rather than as a tick that quietly syncs nothing for a semester.
 */
export async function chooseFolder(id: string, path: string): Promise<Chosen> {
  return (await change(`${CONNECTIONS}/${encodeURIComponent(id)}/selection`, 'PUT', {
    path,
  })) as Chosen
}

/**
 * Unticks one.
 *
 * **Only the syncing stops**, exactly as with removing the connection itself: everything already
 * taken over stays in the library, because it is somebody's material and the connection is only how
 * it got there. The screen says that where the control is, not in a dialog afterwards.
 */
export async function unchooseFolder(id: string, path: string): Promise<void> {
  const at = `${CONNECTIONS}/${encodeURIComponent(id)}/selection?path=${encodeURIComponent(path)}`
  await change(at, 'DELETE')
}

/**
 * What is chosen for a connection that brings a timetable, and what would be offered instead.
 *
 * A read of MyLib's own database, so it costs the other system nothing — unlike {@link whoseTimetable},
 * which signs in over there.
 */
export async function timetablePlan(id: string, signal?: AbortSignal): Promise<TimetablePlan> {
  return ask<TimetablePlan>(`${CONNECTIONS}/${encodeURIComponent(id)}/timetable`, signal)
}

/** Chooses the period, whose plan and the merging — all three together, because all three are read together. */
export async function chooseTimetable(id: string, values: ChosenTimetable): Promise<TimetablePlan> {
  return (await change(
    `${CONNECTIONS}/${encodeURIComponent(id)}/timetable`,
    'PUT',
    values,
  )) as TimetablePlan
}

/**
 * Whose plan these credentials could fetch — asked **before a connection exists**.
 *
 * It is the connection test and the class list in one call, and it stores nothing at all. That is
 * what lets every setting be made on the form before anything is saved, the class included: it is the
 * one setting nobody can know without signing in over there.
 *
 * The password travels in the body, exactly as it does a moment later in the request that keeps the
 * connection. Nothing comes back but the list.
 */
export async function whoseTimetableFor(
  values: NewConnection,
  signal?: AbortSignal,
): Promise<WhoseTimetable> {
  return (await change(
    `${CONNECTIONS}/timetable-elements`,
    'POST',
    values,
    signal,
  )) as WhoseTimetable
}

/**
 * Whose plan this connection could fetch, asked over there with the kept password.
 *
 * **This goes over the wire to somebody else's server**, which is why the screen asks for it once,
 * when the panel is opened, and never again while somebody is typing dates.
 */
export async function whoseTimetable(id: string, signal?: AbortSignal): Promise<WhoseTimetable> {
  return ask<WhoseTimetable>(`${CONNECTIONS}/${encodeURIComponent(id)}/elements`, signal)
}

/**
 * What that installation would call itself, so nobody has to know a value they have never heard of.
 *
 * **This cannot fail.** Not knowing is the ordinary answer — the system may have no such notion, may
 * not be reachable, may not be a system of that kind at all — and every one of those is `null` rather
 * than a refusal. A suggestion that reported failures would put a red line under an address somebody
 * has not finished typing.
 */
export async function suggestedTenant(kind: string, address: string): Promise<string | null> {
  const query = `kind=${encodeURIComponent(kind)}&address=${encodeURIComponent(address)}`
  try {
    const response = await request(`${CONNECTIONS}/tenant-suggestion?${query}`, {
      expect: [...REFUSALS],
    })
    if (!response.ok) {
      return null
    }
    const payload = await readJson<{ tenant?: string | null }>(response)
    return payload.tenant ?? null
  } catch {
    return null
  }
}

/**
 * Which field a refusal is about, or `null` for one that is about none of them.
 *
 * This is the whole point of the service naming three refusals instead of one: a rejected password, an
 * unknown client and an address that does not answer are three different values to correct, and a
 * single banner saying "the connection test failed" would leave somebody guessing which. The mapping
 * lives here, beside the codes it reads, so that the screen only has to ask where to put a sentence.
 */
export type ConnectionField = 'kind' | 'address' | 'tenant' | 'username' | 'password'

const FIELD_OF: Readonly<Record<string, ConnectionField>> = {
  'access-refused': 'password',
  'credentials-cannot-be-opened': 'password',
  'no-such-tenant': 'tenant',
  'not-an-address': 'address',
  'system-not-reachable': 'address',
  'unknown-system': 'kind',
}

export function fieldRefused(failure: Failure): ConnectionField | null {
  const code = failure.code ?? null
  return code === null ? null : (FIELD_OF[code] ?? null)
}

/** How a run went, in the words that stand at the connection. */
export function howItWent(run: SyncRun): string {
  if (run.running) {
    return 'Syncing now'
  }
  switch (run.outcome) {
    case 'FINISHED':
      return 'Finished'
    case 'ACCESS_REFUSED':
      return 'Access refused'
    case 'NOT_REACHABLE':
      return 'Not reachable'
    default:
      // A run that ended without saying how is a run something interrupted. Saying so is better than
      // calling it finished, which would be a claim about material nobody fetched.
      return 'Stopped'
  }
}

/** The statuses that carry a code this side knows how to say out loud. */
const REFUSALS = [400, 404, 409, 501, 502, 503] as const

const SENTENCES: Readonly<Record<string, string>> = {
  'no-such-connection': 'That connection is not there any more.',
  'credentials-cannot-be-opened':
    'MyLib can no longer read the password kept for this connection. Type it in again and everything works as before.',
  'unknown-system': 'This MyLib has no way of reaching that kind of system.',
  'not-an-address':
    'That is not an address MyLib can reach. It starts with https:// and names the server.',
  'access-refused':
    'That system did not accept these credentials. Either the user name or the password is not right.',
  'no-such-tenant': 'That system does not know this client.',
  'system-not-reachable': 'That system did not answer.',
  /*
    Said the same way whether somebody typed the path or walked to it, because it is the same
    answer: a slip in a typed path and a folder that has since been moved over there are both "there
    is nothing there", and inventing two sentences would be inventing a distinction MyLib cannot see.
  */
  'no-such-path': 'There is nothing at that path in the other system.',
  /*
    Not a fault and not a missing thing: the request was fine and this connection is the wrong thing to
    make it of. The screen draws the other panel rather than reporting anything.
  */
  'system-has-no-folders': 'That system brings a timetable rather than folders.',
  'system-has-no-timetable': 'That system brings folders rather than a timetable.',
  'not-a-time': 'That is not a day MyLib can read.',
}

/**
 * Asks for something, with the refusals of this area read out loud.
 *
 * Apart from {@link change} because it carries the caller's abort signal and sends no body: a level
 * of a foreign tree is loaded by a screen that can go away mid-request, and a component that has gone
 * is not a failure. What the two share is {@link refusalIn} — a `no-such-path` has to mean the same
 * sentence whether it arrives from browsing or from ticking.
 */
async function ask<T>(address: string, signal?: AbortSignal): Promise<T> {
  const response = await request(address, { signal, expect: [...REFUSALS] })
  if (!response.ok) {
    throw refusalIn(await response.text(), response.status)
  }
  return readJson<T>(response)
}

async function change(
  address: string,
  method: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<unknown> {
  const response = await request(address, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    expect: [...REFUSALS],
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal === undefined ? {} : { signal }),
  })

  if (!response.ok) {
    throw refusalIn(await response.text(), response.status)
  }

  return response.status === 204 ? null : readJson<unknown>(response)
}

function parse(body: string): { code?: string; message?: string } | null {
  try {
    return JSON.parse(body) as { code?: string; message?: string }
  } catch {
    return null
  }
}

function refusalIn(body: string, status: number): RequestFailed {
  const problem = parse(body)
  const known = problem?.code === undefined ? undefined : SENTENCES[problem.code]

  const failure: Failure = {
    /*
      Every code on that list is an *answer about this connection*, whatever status carries it: a
      system that did not answer arrives as 502 and is still something somebody can act on, not a
      MyLib that broke. Only an unnamed 5xx is this service's own fault.
    */
    kind: known === undefined && status >= 500 ? 'service' : 'refused',
    message: known ?? problem?.message ?? 'MyLib could not do that.',
    detail: known === undefined ? null : (problem?.message ?? null),
    /*
      One of them gets better by being tried again, and only one: a system that could not be reached
      a moment ago may well answer now. A rejected password, an unknown client and an installation
      with no key for credentials are each as true the second time as the first.
    */
    retryable: problem?.code === 'system-not-reachable',
    code: problem?.code ?? null,
  }
  return new RequestFailed(failure)
}
